import * as turf from "@turf/turf";
import {
  createRegion,
  getProjectAnalysisTypes,
  getProjectCharts,
  getTaskResults,
} from "@/api/rest";
import type { Chart, AnalysisType, TaskResult } from "@/types";
import { defineStore } from "pinia";
import { ref, watch } from "vue";
import { useFramePreviewStore } from "./framePreview";
import {
  useProjectStore,
  useMapStore,
  useLayerStore,
  useNetworkStore,
} from ".";
import { TerraDraw, TerraDrawPolygonMode } from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";

export const useAnalysisStore = defineStore("analysis", () => {
  const projectStore = useProjectStore();
  const mapStore = useMapStore();
  const layerStore = useLayerStore();
  const networkStore = useNetworkStore();

  const loadingCharts = ref<boolean>(false);
  const availableCharts = ref<Chart[]>();
  const currentChart = ref<Chart>();
  const currentAnalysisTab = ref<"old" | "new">("new");
  const loadingAnalysisTypes = ref<boolean>(false);
  const availableAnalysisTypes = ref<AnalysisType[]>();
  const currentAnalysisType = ref<AnalysisType>();
  const availableResults = ref<TaskResult[]>([]);
  const currentResult = ref<TaskResult>();
  const selectedInputs = ref<Record<string, any>>({});
  const inputOptionFiltering = ref<Record<string, boolean>>({});
  const filteredInputOptions = ref<Record<string, any>>({});
  const terradraw = ref<TerraDraw | undefined>(undefined);
  const drawingRegion = ref<boolean>(false);
  const drawingRegionForInput = ref<undefined | string>();
  const drawnRegionCoords = ref<number[][][] | undefined>();
  const newRegionName = ref<string | undefined>();
  const ws = ref();

  async function initCharts(projectId: number) {
    loadingCharts.value = true;
    const charts = await getProjectCharts(projectId);
    availableCharts.value = charts;
    currentChart.value = undefined;
    loadingCharts.value = false;
  }

  async function initAnalysisTypes(projectId: number) {
    loadingAnalysisTypes.value = true;
    const types = await getProjectAnalysisTypes(projectId);
    availableAnalysisTypes.value = types;
    currentAnalysisType.value = undefined;
    loadingAnalysisTypes.value = false;
  }

  async function initResults(analysisType: string, projectId: number) {
    availableResults.value = await getTaskResults(analysisType, projectId);
  }

  async function fetchResults() {
    if (!projectStore.currentProject || !currentAnalysisType.value) return;
    await initResults(
      currentAnalysisType.value.db_value,
      projectStore.currentProject.id,
    );
    if (currentResult.value) {
      currentResult.value = availableResults.value.find(
        (r) => r.id === currentResult.value?.id,
      );
    }
  }

  function initSelectedInputs() {
    const type = currentAnalysisType.value;
    selectedInputs.value = {};
    if (type) {
      Object.keys(type.input_types).forEach((key) => {
        if (type.input_defaults[key]) {
          selectedInputs.value[key] = type.input_defaults[key];
        } else if (inputIsNumeric(key)) {
          selectedInputs.value[key] = type.input_options[key][0].min;
        } else {
          const inputType = type.input_types[key].toLowerCase();
          const optionsIds = type.input_options[key].map((c: any) => c.id);
          const currentFrames = layerStore.selectedLayers
            .filter((l) => l.visible)
            .map((l) => ({
              ...layerStore.framesByLayerId[l.id][l.current_frame_index],
              frame_index: l.current_frame_index,
            }));
          if (inputType === "rasterdata") {
            const currentRasterFrame = currentFrames.find((f) => f.raster);
            if (
              currentRasterFrame?.raster &&
              optionsIds.includes(currentRasterFrame.raster.id)
            ) {
              selectedInputs.value[key] = currentRasterFrame.raster.id;
              if (
                currentRasterFrame.raster.metadata?.frames?.length &&
                currentRasterFrame.frame_index
              ) {
                selectedInputs.value[key + "_frame"] =
                  currentRasterFrame.frame_index;
              }
            }
          } else if (inputType === "vectordata") {
            const currentVectorFrame = currentFrames.find((f) => f.vector);
            if (
              currentVectorFrame?.vector &&
              optionsIds.includes(currentVectorFrame.vector.id)
            ) {
              selectedInputs.value[key] = currentVectorFrame.vector.id;
            }
          } else if (inputType === "network") {
            if (
              networkStore.currentNetwork &&
              optionsIds.includes(networkStore.currentNetwork.id)
            ) {
              selectedInputs.value[key] = networkStore.currentNetwork.id;
            } else {
              const networksByVectorId = Object.fromEntries(
                networkStore.availableNetworks.map((n) => [n.vector_data, n]),
              );
              const currentNetworkFrame = currentFrames.find(
                (f) => f.vector && networksByVectorId[f.vector.id],
              );
              if (currentNetworkFrame && currentNetworkFrame.vector) {
                const currentNetwork =
                  networksByVectorId[currentNetworkFrame.vector.id];
                if (optionsIds.includes(currentNetwork.id))
                  selectedInputs.value[key] = currentNetwork.id;
              }
            }
          } else if (
            inputType === "chart" &&
            currentChart.value &&
            optionsIds.includes(currentChart.value.id)
          ) {
            selectedInputs.value[key] = currentChart.value.id;
          }
        }
      });
    }
  }

  function initInputOptionFiltering() {
    inputOptionFiltering.value = currentAnalysisType.value
      ? Object.fromEntries(
          Object.entries(currentAnalysisType.value.input_types).map(
            ([key, inputType]) => [key, inputType === "Region"],
          ),
        )
      : {};
    filteredInputOptions.value = {};
  }

  function filterInputOptions() {
    if (!currentAnalysisType.value || !mapStore.currentMapBounds) return;
    for (const key in inputOptionFiltering.value) {
      if (inputOptionFiltering.value[key]) {
        const type = currentAnalysisType.value.input_types[key];
        if (type.toLocaleLowerCase() === "region") {
          const turfBounds = turf.bboxPolygon([
            mapStore.currentMapBounds.getWest(),
            mapStore.currentMapBounds.getSouth(),
            mapStore.currentMapBounds.getEast(),
            mapStore.currentMapBounds.getNorth(),
          ]);
          filteredInputOptions.value[key] =
            currentAnalysisType.value.input_options[key].filter(
              (opt: any) =>
                opt.id === selectedInputs.value[key] ||
                turf.booleanIntersects(
                  turfBounds,
                  turf.multiPolygon(opt.boundary),
                ),
            );
        }
      } else {
        filteredInputOptions.value[key] = undefined;
      }
    }
  }

  function inputIsNumeric(key: string) {
    return (
      currentAnalysisType.value &&
      currentAnalysisType.value.input_types[key] === "number" &&
      currentAnalysisType.value.input_options[key].length == 1 &&
      currentAnalysisType.value.input_options[key][0].min !== undefined &&
      currentAnalysisType.value.input_options[key][0].max !== undefined &&
      currentAnalysisType.value.input_options[key][0].step !== undefined
    );
  }

  function cancelDraw() {
    if (terradraw.value) {
      terradraw.value?.setMode("static");
      terradraw.value.clear();
    }
    drawingRegion.value = false;
    drawingRegionForInput.value = undefined;
    drawnRegionCoords.value = undefined;
    newRegionName.value = undefined;
  }

  function drawNewRegion(inputName: string) {
    drawingRegion.value = true;
    drawingRegionForInput.value = inputName;
    const map = mapStore.getMap();
    if (!terradraw.value) {
      terradraw.value = new TerraDraw({
        adapter: new TerraDrawMapLibreGLAdapter({ map }),
        modes: [new TerraDrawPolygonMode()],
      });
      terradraw.value.on("finish", () => {
        terradraw.value?.setMode("static");
        const snapshot = terradraw.value?.getSnapshot();
        if (snapshot?.length) {
          drawnRegionCoords.value = snapshot[0].geometry
            .coordinates as number[][][];
        }
        // Only unset drawingRegion after click callbacks have completed
        setTimeout(() => (drawingRegion.value = false), 1);
      });
    }
    terradraw.value.start();
    // Ensure that terradraw layers are on top
    map.getStyle().layers.forEach((layer) => {
      if (layer.id.startsWith("td-")) {
        map.moveLayer(layer.id);
      }
    });
    terradraw.value.clear();
    terradraw.value.setMode("polygon");
  }

  function saveNewRegion() {
    if (
      !newRegionName.value ||
      !drawnRegionCoords.value ||
      !projectStore.currentProject
    )
      return;
    createRegion({
      name: newRegionName.value,
      project_id: projectStore.currentProject.id,
      boundary: [drawnRegionCoords.value],
      metadata: {
        source: "Drawn on map via UI",
      },
    }).then(async (region) => {
      if (!projectStore.currentProject || !drawingRegionForInput.value) return;
      availableAnalysisTypes.value = await getProjectAnalysisTypes(
        projectStore.currentProject.id,
      );
      const matchingAnalysisType = availableAnalysisTypes.value.find(
        (analysisType) =>
          analysisType.db_value === currentAnalysisType.value?.db_value,
      );
      if (currentAnalysisType.value && matchingAnalysisType)
        currentAnalysisType.value.input_options =
          matchingAnalysisType.input_options;
      selectedInputs.value[drawingRegionForInput.value] = region.id;
      drawingRegionForInput.value = undefined;
      newRegionName.value = undefined;
      drawnRegionCoords.value = undefined;
      terradraw.value?.clear();
    });
  }

  function createWebSocket() {
    if (ws.value) ws.value.close();
    if (projectStore.currentProject) {
      const urlBase = `${import.meta.env.VITE_API_ROOT}ws/`;
      const url = `${urlBase}analytics/project/${projectStore.currentProject.id}/results/`;
      ws.value = new WebSocket(url);
      ws.value.onmessage = (event: any) => {
        const data = JSON.parse(JSON.parse(event.data));
        if (data.task_type === "frame_preview" && data.completed) {
          // Regenerated previews are ready; reload and reattach them to layers.
          useFramePreviewStore().onPreviewTaskComplete(data);
        }
        if (currentResult.value && data.id === currentResult.value.id) {
          // only overwrite attributes expecting updates
          // overwriting the whole currentResult object will cause
          // the expansion panel to collapse
          currentResult.value.error = data.error;
          currentResult.value.outputs = data.outputs;
          currentResult.value.status = data.status;
          currentResult.value.completed = data.completed;
          currentResult.value.name = data.name;
          availableResults.value = availableResults.value.map((result) =>
            result.id === data.id ? data : result,
          );
        }
        if (data.completed && projectStore.currentProject) {
          // completed result object may become an input option
          // for another analysis type, refresh available types
          getProjectAnalysisTypes(projectStore.currentProject.id).then(
            (types) => {
              availableAnalysisTypes.value = types;
            },
          );
        }
      };
    }
  }

  watch(currentAnalysisType, () => {
    fetchResults();
    initSelectedInputs();
    initInputOptionFiltering();
  });

  watch(() => projectStore.currentProject, createWebSocket);
  watch(() => mapStore.currentMapBounds, filterInputOptions);
  watch(inputOptionFiltering, filterInputOptions, { deep: true });

  return {
    loadingCharts,
    availableCharts,
    currentChart,
    currentAnalysisTab,
    loadingAnalysisTypes,
    availableAnalysisTypes,
    currentAnalysisType,
    availableResults,
    currentResult,
    selectedInputs,
    inputOptionFiltering,
    filteredInputOptions,
    initInputOptionFiltering,
    filterInputOptions,
    initCharts,
    initAnalysisTypes,
    initResults,
    fetchResults,
    initSelectedInputs,
    inputIsNumeric,
    drawingRegion,
    drawingRegionForInput,
    drawnRegionCoords,
    newRegionName,
    cancelDraw,
    drawNewRegion,
    saveNewRegion,
  };
});
